import { gql, useQuery } from '@apollo/client'
import { ForbiddenError } from '../components/ErrorBoundary'
import { StructParams, StructureBase, getChildType, QueryStructParams, VDCounts } from '../lib/structures'
import type { Timeline } from '../types/Timeline'
import { useEffect } from 'react'

interface MicroTargetingDataForChildren {
  phase1: {
    current: number
    target: number
    historic: number
  }
  phase2: {
    current: number
    target: number
    historic: number
  }
  phase3: {
    current: number
    target: number
    historic: number
  }
  phase1and2: number
  phaseAll: number
  vdcounts: {
    total: number
    included: number
  }
}

interface MicroTargetingDataForSelf extends MicroTargetingDataForChildren {
  historic: Timeline<number>
}

export interface StructureWithMicroTargeting extends StructureWithMicroTargetingBase {
  microtargeting: MicroTargetingDataForSelf
  parents: StructureBase[]
  children: StructureWithMicroTargetingBase[]
}

interface StructureReturns {
  stats: StructureWithMicroTargeting | undefined
  loading: boolean
}

interface StructureWithMicroTargetingBase extends StructureBase {
  microtargeting: MicroTargetingDataForChildren
  counts: VDCounts
}

interface MicroTargetingResult {
  structure: StructureWithMicroTargeting
}

const query = gql`
query getMicroTargetingData($type: String!, $code: String!, $childType: String!, $filterLabel: String) {
  structure(type: $type, code: $code, filterLabel: $filterLabel) {
    type
    code
    name
    responsiblePersons { firstname surname displayname }
    userHasAccess
    structureHasAssignedWards
    counts {
      total
      filtered
    }
    microtargeting {
        phase1 { 
          current
          target
          historic {
            date
            count
            }
        }
        phase2 { 
          current
          target
          historic {
            date
            count
            }
        }
        phase3 {
          current
          target
          historic {
            date
            count
            }
        }
        phase1and2
        phaseAll
        vdcounts {
          total
          included
          }
      }
    parents { type code name userHasAccess }
    children(type: $childType) {
      type
      code
      name
      responsiblePersons { firstname surname displayname }
      userHasAccess
      structureHasAssignedWards
      counts {
        total
        filtered
      }
      microtargeting {
        phase1{
          current
          target
          historic {
            date
            count   
            }
        }
        phase2{
          current
          target
          historic  {
            date
            count
            }
        }
        phase3 {
          current
          target
          historic  {
            date
            count
          }
        }
        phase1and2
        phaseAll 
        vdcounts {
          total
          included
          }        
      }
    }
  }
}
`

export function useMicroTargeting ({ type, code, filterLabel }: StructParams): StructureReturns {
  const childType = getChildType({ type, code })
  const { error, data, refetch, loading } = useQuery<MicroTargetingResult, QueryStructParams>(
    query, { variables: { type, code, childType, filterLabel }, notifyOnNetworkStatusChange: true }
  )

  useEffect(() => {
    refetch()
  }, [filterLabel, refetch])

  if (error?.graphQLErrors[0]?.extensions?.code === 'FORBIDDEN') {
    throw new ForbiddenError('this structure')
  }
  if (error != null) throw error

  return { stats: data?.structure, loading: loading }
}
